import React, { createContext, useContext, useCallback, useState, useMemo } from 'react';
import { MatterInvoiceDetail } from '../../types';

type MatterARContextType = {
  isOpen: boolean;
  openMatterDrawer: (
    matterId: string,
    clientId: string,
    daysPastDue?: number,
    daysPastDueCap?: number
  ) => void;
  closeMatterDrawer: () => void;
  openInvoiceDetail: (invoice: MatterInvoiceDetail) => void;
  closeInvoiceDetail: () => void;
  currentMatterId: string | null;
  currentClientId: string | null;
  currentInvoice: MatterInvoiceDetail | null;
  daysPastDue: number | null;
  daysPastDueCap: number | null;
};

const MatterARContext = createContext<MatterARContextType | undefined>(undefined);
// @Todo - set invoice type any to Invoice
export const MatterArDetailProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentMatterId, setCurrentMatterId] = useState<string | null>(null);
  const [currentClientId, setCurrentClientId] = useState<string | null>(null);
  const [currentInvoice, setCurrentInvoice] = useState<MatterInvoiceDetail | null>(null);
  const [daysPastDue, setDaysPastDue] = useState<number | null>(null);
  const [daysPastDueCap, setDaysPastDueCap] = useState<number | null>(null);
  const openMatterDrawer = useCallback(
    (matterId: string, clientId: string, daysPastDue?: number, daysPastDueCap?: number) => {
      setCurrentMatterId(matterId);
      setCurrentClientId(clientId);
      setDaysPastDue(daysPastDue ?? null);
      setDaysPastDueCap(daysPastDueCap ?? null);
    },
    []
  );

  const closeMatterDrawer = useCallback(() => {
    setCurrentMatterId(null);
    setCurrentClientId(null);
    setDaysPastDue(null);
    setDaysPastDueCap(null);
  }, []);

  const openInvoiceDetail = useCallback((invoice: any) => {
    setCurrentInvoice(invoice);
  }, []);

  const closeInvoiceDetail = useCallback(() => {
    setCurrentInvoice(null);
  }, []);

  const value = useMemo(
    () => ({
      isOpen: currentMatterId !== null,
      openMatterDrawer,
      closeMatterDrawer,
      openInvoiceDetail,
      closeInvoiceDetail,
      currentMatterId,
      currentClientId,
      currentInvoice,
      daysPastDue,
      daysPastDueCap,
    }),
    [
      currentMatterId,
      currentClientId,
      currentInvoice,
      openMatterDrawer,
      closeMatterDrawer,
      openInvoiceDetail,
      closeInvoiceDetail,
      daysPastDue,
      daysPastDueCap,
    ]
  );

  return <MatterARContext.Provider value={value}>{children}</MatterARContext.Provider>;
};

export const useMatterARContext = (): MatterARContextType => {
  const context = useContext(MatterARContext);
  if (!context) {
    throw new Error('useMatterARContext must be used within a MatterARProvider');
  }
  return context;
};
